// Pure view helpers for the League Table module — ported from
// js/renderers-leaguetable.js. Kept separate from the component so the format +
// selection logic is unit-tested without a DOM.

import type { ProtoFixture } from "@catbento/shared";
import type { CompetitionId, LeagueChoice } from "../data/league.js";

/**
 * Humanise a season code for display — ported verbatim from the renderer
 * (js/renderers-leaguetable.js:11). A "5"-prefixed 5-digit code is a two-calendar-
 * year European season → "25/26"; anything else (NWSL's calendar-year code) is shown
 * as-is.
 */
export function humanizeSeasonCode(code: string): string {
  const str = String(code);
  if (str.length === 5 && str.charAt(0) === "5") {
    const year = parseInt(str.slice(1), 10);
    const pad2 = (n: number) => String(n % 100).padStart(2, "0");
    return `${pad2(year)}/${pad2(year + 1)}`;
  }
  return str;
}

/**
 * The competition a freshly-rendered module opens on: the one it had (undo/preset
 * restore via mod._leagueTableCompetition) if still captured, else (?live=1) the current
 * player's league, else the first — the renderer's own fallback (js/renderers-leaguetable.js:76).
 */
export function resolveInitialCompetition(
  previous: CompetitionId | null | undefined,
  competitions: LeagueChoice[],
  // ?live=1: the current player's league (useStickyClub); null on the snapshot site.
  stickyLeague: CompetitionId | null = null,
): CompetitionId | null {
  const has = (id: CompetitionId | null | undefined) => id != null && competitions.some((c) => c.id === id);
  if (has(previous)) return previous as CompetitionId;
  if (has(stickyLeague)) return stickyLeague;
  return competitions[0]?.id ?? null;
}

/**
 * The league a League Table / Match Results / Squad shows, given whether it was just told to
 * follow a linked player change (`_followPlayer`, canvas/league-follow.ts):
 * - following: the player's league if listed; else it stays where it was and `skipped` is
 *   true (the module flashes, like a player module that can't take the pick);
 * - otherwise: `resolveInitialCompetition`.
 */
export function resolveFollowCompetition(
  previous: CompetitionId | null | undefined,
  competitions: LeagueChoice[],
  stickyLeague: CompetitionId | null,
  follow: boolean,
): { competition: CompetitionId | null; followed: boolean; skipped: boolean } {
  if (follow && stickyLeague != null && competitions.some((c) => c.id === stickyLeague)) {
    return { competition: stickyLeague, followed: true, skipped: false };
  }
  return { competition: resolveInitialCompetition(previous, competitions, stickyLeague), followed: false, skipped: follow };
}

/** The label a league is shown by in its picker — for the widgets' "No … for FAWSL" lines. */
export function competitionLabel(competitions: LeagueChoice[], id: CompetitionId | null): string {
  const choice = competitions.find((c) => c.id === id);
  // Only ever asked for a league the widget picked from this same list.
  if (!choice) throw new Error(`League ${String(id)} isn't in the picker's list`);
  return choice.label;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** One game in a team's form guide. */
export interface FormGame {
  result: "W" | "D" | "L";
  /** "W 2–1 vs Arsenal (H) · 14 Oct" — the dot's hover. */
  text: string;
  /** The game itself — a box drags out as that match (Ben, 2026-10-31). */
  fixture: ProtoFixture;
}

/** A team's last `n` results, oldest → newest (the newest on the right, as form guides read) —
 *  the League Table's form column (Ben, 2026-10-18: Catalyst's table has none). By team name, as
 *  the table's rows. Pure. */
export function teamForm(fixtures: readonly ProtoFixture[], team: string, n = 5): FormGame[] {
  return fixtures
    .filter((f) => f.homeTeamName === team || f.awayTeamName === team)
    .sort((a, b) => (a.matchDate < b.matchDate ? -1 : a.matchDate > b.matchDate ? 1 : a.matchId - b.matchId))
    .slice(-n)
    .map((f) => {
      const home = f.homeTeamName === team;
      const us = home ? f.homeScore : f.awayScore;
      const them = home ? f.awayScore : f.homeScore;
      const result = us > them ? "W" : us < them ? "L" : "D";
      const d = new Date(f.matchDate);
      const date = `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
      return { result, text: `${result} ${us}–${them} vs ${home ? f.awayTeamName : f.homeTeamName} (${home ? "H" : "A"}) · ${date}`, fixture: f };
    });
}
